import { call } from '@/utils/yambda';
import clsx from 'clsx';
import React, { cloneElement, forwardRef, isValidElement } from 'react';

// TODO: BaseButton을 사용하면 css가 적용되지 않는 문제가 있음.
// 우선 BaseButtonForRectButton을 사용하도록 함. 개선 필요.
import BaseButton, { BaseLinkButtonProps, BaseRealButtonProps } from './BaseButtonForRectButton/BaseButton';
import styles from './RectButton.module.scss';

type ChildrenSet = {
  icon: JSX.Element;
  label: string;
};

type Children =
  | string // Text
  | JSX.Element // Icon
  | ChildrenSet; // Icon + Text

type PseudoButtonProps = {
  children: Children;
} & Omit<BaseLinkButtonProps, 'children'>;

type RealButtonProps = {
  children: Children;
} & Omit<BaseRealButtonProps, 'children'>;

const neitherStringNorElement = (s: Children): s is ChildrenSet => typeof s !== 'string' && !isValidElement(s);

const injectIconClass = (element: React.ReactElement<{ className: string }>): React.ReactElement =>
  isValidElement(element)
    ? cloneElement<{ className: string }>(element, {
        className: clsx(element.props.className, styles.icon),
      })
    : element;

const rectButtonFactory = (
  type: 'primary' | 'secondary' | 'basic' | 'blue',
): React.FC<RealButtonProps | PseudoButtonProps> => {
  const RectButtonComp: React.ForwardRefRenderFunction<HTMLElement, RealButtonProps | PseudoButtonProps> = (
    { children, className, disabled, ...props },
    ref,
  ) => {
    const klass = clsx(styles[type], disabled && styles.disabled, className);

    // 아이콘이 존재하면 styles.icon 주입해 반환. 텍스트는 바로 통과.
    const theChildren = call(() => {
      if (neitherStringNorElement(children)) {
        return (
          <>
            {injectIconClass(children.icon)}
            <span className={styles.label}>{children.label}</span>
          </>
        );
      }

      if (typeof children === 'string') {
        return <span className={styles.label}>{children}</span>;
      }

      return injectIconClass(children as JSX.Element);
    });

    return (
      <BaseButton ref={ref} className={klass} disabled={disabled} {...props}>
        {theChildren}
      </BaseButton>
    );
  };
  RectButtonComp.displayName = `RectButton-${type}`;

  return forwardRef(RectButtonComp);
};

/**
 * 사각형 버튼, 혹은 링크. 텍스트, 아이콘, 또는 둘 다 포함 가능.
 *
 * @see https://zpl.io/aMNgMqd
 * @example
 * const viewAll = (
 *   <RectButton.Primary as="link" className={styles.viewAll} href="/" onClick={trackClickViewAll}>
 *     전체 보기
 *   </RectButton.Primary>
 * )
 *
 * const deleteAll = (
 *   <RectButton.Basic as="button" className={styles.deleteAll} onClick={store.deleteAll}>
 *     {{
 *       icon: <IconCommon type="icon_trashcan" />,
 *       label: "모두 삭제"
 *     }}
 *   </RectButton.Basic>
 * )
 *
 * const favIt = (
 *   <RectButton.Basic as="button" className={styles.fav} onClick={store.deleteAll}>
 *     <IconCommon type="favorite" />
 *   </RectButton.Basic>
 * )
 */
const RectButton = {
  /** 파랑. */
  Primary: rectButtonFactory('primary'),
  /** 검정, 라인. */
  Secondary: rectButtonFactory('secondary'),
  /** 회색, 라인. */
  Basic: rectButtonFactory('basic'),
  /** 파랑, 라인. */
  Blue: rectButtonFactory('blue'),
};

export default RectButton;
